import { useState } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { UploadSimple, TextAlignLeft, ArrowRight, ArrowLeft, CheckCircle, Warning } from "@phosphor-icons/react";

const CHUNK = 400 * 1024; // 400 Ko par morceau

export default function DomainImportWizard({ open, onOpenChange, onDone }) {
  const [step, setStep] = useState(0); // 0 upload, 1 mapping, 2 preview, 3 done
  const [mode, setMode] = useState("file");
  const [csvText, setCsvText] = useState("");
  const [sessionId, setSessionId] = useState(null);
  const [uploadPct, setUploadPct] = useState(0);
  const [paste, setPaste] = useState("");
  const [analysis, setAnalysis] = useState(null);
  const [mapping, setMapping] = useState({});
  const [hasHeader, setHasHeader] = useState(true);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const reset = () => { setStep(0); setCsvText(""); setSessionId(null); setUploadPct(0); setPaste(""); setAnalysis(null); setMapping({}); setPreview(null); setResult(null); };
  const close = (v) => { onOpenChange(v); if (!v) setTimeout(reset, 200); };

  const readChunkB64 = (blob) => new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result); // data:...;base64,XXX
    r.onerror = reject;
    r.readAsDataURL(blob);
  });

  // Upload chunké : le fichier n'est envoyé qu'UNE fois, réutilisé pour aperçu + import.
  const onFile = async (file) => {
    if (!file) return;
    setBusy(true); setUploadPct(0); setCsvText("");
    try {
      const { data: init } = await api.post("/admin/company-directory/import/upload/init", {});
      const sid = init.session_id;
      const total = file.size || 1;
      let sent = 0;
      for (let start = 0; start < file.size; start += CHUNK) {
        const blob = file.slice(start, Math.min(start + CHUNK, file.size));
        const dataUrl = await readChunkB64(blob);
        await api.post("/admin/company-directory/import/upload/chunk", { session_id: sid, data: dataUrl });
        sent += blob.size;
        setUploadPct(Math.round((sent / total) * 100));
      }
      const { data } = await api.post("/admin/company-directory/import/upload/complete", { session_id: sid });
      setSessionId(sid);
      setAnalysis(data); setMapping(data.suggested || {}); setHasHeader(data.has_header); setStep(1);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Upload impossible"); }
    finally { setBusy(false); }
  };

  const usePaste = () => {
    // "Nom | domaine" ou "Nom,domaine" -> CSV standard
    const lines = paste.split("\n").map((l) => l.trim()).filter(Boolean);
    const rows = lines.map((l) => {
      const parts = l.split(/[|,;\t]/).map((p) => p.trim());
      return `${(parts[0] || "").replace(/"/g, "")},${(parts[1] || "").replace(/"/g, "")}`;
    });
    const text = "company_name,domain\n" + rows.join("\n");
    setCsvText(text); setSessionId(null);
    analyze(text);
  };

  const analyze = async (text) => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/company-directory/import/analyze", { csv: text });
      setAnalysis(data); setMapping(data.suggested || {}); setHasHeader(data.has_header); setStep(1);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const importBody = (extra = {}) => (sessionId
    ? { session_id: sessionId, mapping, has_header: hasHeader, ...extra }
    : { csv: csvText, mapping, has_header: hasHeader, ...extra });

  const runPreview = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/company-directory/import/preview", importBody());
      setPreview(data); setStep(2);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const commit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/company-directory/import/commit", importBody({ label: mode === "paste" ? "Correspondances manuelles" : "Import CSV Prospeo" }));
      setResult(data.summary); setStep(3);
      toast.success(`Import terminé : ${data.summary.enriched} enrichies, ${data.summary.created} créées.`);
      onDone?.();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const setMap = (field, idx) => setMapping((m) => ({ ...m, [field]: idx === "-1" ? undefined : Number(idx) }));

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="bg-surface border-border max-w-3xl p-0 gap-0 overflow-hidden" data-testid="domain-import-wizard">
        <DialogTitle className="sr-only">Importer un CSV de domaines</DialogTitle>
        <DialogDescription className="sr-only">Réimportez un CSV Prospeo enrichi pour renseigner les domaines des sociétés.</DialogDescription>
        <div className="px-6 pt-5 pb-4 border-b border-border flex items-center gap-2">
          <UploadSimple size={16} className="text-accent" weight="fill" />
          <h2 className="font-display font-bold text-fg">Importer les domaines enrichis</h2>
        </div>

        <div className="px-6 py-5 max-h-[68vh] overflow-y-auto">
          {/* STEP 0 — source */}
          {step === 0 && (
            <div data-testid="import-step-source">
              <div className="flex gap-2 mb-4">
                <button onClick={() => setMode("file")} className={`flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-md border ${mode === "file" ? "border-accent/40 text-accent bg-accent/10" : "border-border text-muted"}`} data-testid="mode-file"><UploadSimple size={14} /> Fichier CSV</button>
                <button onClick={() => setMode("paste")} className={`flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-md border ${mode === "paste" ? "border-accent/40 text-accent bg-accent/10" : "border-border text-muted"}`} data-testid="mode-paste"><TextAlignLeft size={14} /> Coller</button>
              </div>
              {mode === "file" ? (
                <label className="block border-2 border-dashed border-border rounded-lg p-8 text-center cursor-pointer hover:border-accent/40" data-testid="import-dropzone">
                  <UploadSimple size={28} className="text-accent mx-auto mb-2" />
                  {busy && uploadPct > 0 ? (
                    <>
                      <div className="text-sm text-fg">Envoi du fichier… {uploadPct}%</div>
                      <div className="h-1.5 bg-surface-2 rounded-full mt-2 overflow-hidden max-w-xs mx-auto">
                        <div className="h-full bg-accent transition-all" style={{ width: `${uploadPct}%` }} data-testid="upload-progress" />
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="text-sm text-fg">Cliquez pour choisir un fichier CSV</div>
                      <div className="text-xs text-muted mt-1">Export Prospeo complet ou fichier simple « company_name,domain ». Fichiers volumineux (20k+ lignes) gérés : envoi une seule fois, réutilisé pour l'aperçu et l'import. L'ID interKO sert de clé de matching quand présent.</div>
                    </>
                  )}
                  <input type="file" accept=".csv,text/csv" className="hidden" data-testid="import-file-input" onChange={(e) => onFile(e.target.files?.[0])} />
                </label>
              ) : (
                <div>
                  <textarea value={paste} onChange={(e) => setPaste(e.target.value)} rows={8} data-testid="import-paste-area"
                    placeholder={"Capgemini | capgemini.com\nKiabi | kiabi.com\nDecathlon | decathlon.fr"}
                    className="w-full bg-surface-2 border border-border rounded-md p-3 text-sm text-fg font-mono outline-none focus:border-accent/40" />
                  <button onClick={usePaste} disabled={!paste.trim() || busy} data-testid="import-paste-submit"
                    className="mt-3 flex items-center gap-2 bg-accent text-black font-semibold px-4 py-2 rounded-md hover-lift disabled:opacity-50">
                    Analyser <ArrowRight size={15} />
                  </button>
                </div>
              )}
            </div>
          )}

          {/* STEP 1 — mapping */}
          {step === 1 && analysis && (
            <div data-testid="import-step-mapping">
              <p className="text-sm text-muted mb-3">{analysis.total} ligne(s) détectée(s). Vérifiez la correspondance des colonnes.</p>
              <label className="flex items-center gap-2 text-xs text-muted mb-3 cursor-pointer">
                <input type="checkbox" checked={hasHeader} onChange={(e) => setHasHeader(e.target.checked)} className="accent-[#00E5FF]" data-testid="import-has-header" />
                La première ligne est un en-tête
              </label>
              <div className="grid sm:grid-cols-2 gap-3">
                {analysis.fields.map((f) => (
                  <div key={f.key} className="flex items-center gap-2">
                    <span className="text-xs text-muted w-32 shrink-0">{f.label}{f.required && <span className="text-[#EF4444]"> *</span>}</span>
                    <Select value={mapping[f.key] != null ? String(mapping[f.key]) : "-1"} onValueChange={(v) => setMap(f.key, v)}>
                      <SelectTrigger className="h-8 bg-surface-2 border-border text-xs flex-1" data-testid={`map-${f.key}`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="-1">— Ignorer —</SelectItem>
                        {analysis.columns.map((col, i) => <SelectItem key={i} value={String(i)}>{col}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                ))}
              </div>
              {analysis.sample?.length > 0 && (
                <div className="mt-4 overflow-x-auto">
                  <div className="overline mb-1">Aperçu</div>
                  <table className="text-xs border border-border rounded"><tbody>
                    {analysis.sample.slice(0, 4).map((r, ri) => (
                      <tr key={ri} className="border-b border-border/40">{r.map((cell, ci) => <td key={ci} className="px-2 py-1 text-muted whitespace-nowrap max-w-[160px] truncate">{cell}</td>)}</tr>
                    ))}
                  </tbody></table>
                </div>
              )}
              <div className="mt-5 flex items-center gap-3">
                <button onClick={() => setStep(0)} className="text-xs text-muted hover:text-fg flex items-center gap-1"><ArrowLeft size={12} /> Retour</button>
                <button onClick={runPreview} disabled={busy || mapping.company_name == null} data-testid="import-preview-btn"
                  className="ml-auto flex items-center gap-2 bg-accent text-black font-semibold px-4 py-2 rounded-md hover-lift disabled:opacity-50">
                  {busy ? "Analyse…" : "Prévisualiser"} <ArrowRight size={15} />
                </button>
              </div>
            </div>
          )}

          {/* STEP 2 — preview */}
          {step === 2 && preview && (
            <div data-testid="import-step-preview">
              <div className="grid grid-cols-3 gap-3 mb-4">
                <Pv label="Total" value={preview.counts.total} />
                <Pv label="Sociétés enrichies" value={preview.counts.enrich} color="#22C55E" />
                <Pv label="Nouvelles sociétés" value={preview.counts.new} color="#00E5FF" />
                <Pv label="Domaine identique" value={preview.counts.same} />
                <Pv label="Conflits" value={preview.counts.conflict} color="#F97316" />
                <Pv label="Invalides / job-boards" value={preview.counts.invalid} color="#EF4444" />
              </div>
              {preview.conflicts?.length > 0 && (
                <div className="bg-[#F97316]/10 border border-[#F97316]/30 rounded-md p-3 mb-3 text-xs text-[#F5C877]">
                  <div className="flex items-center gap-1.5 mb-1 font-semibold"><Warning size={13} /> {preview.conflicts.length} conflit(s) — ne seront PAS écrasés, mis en file « À vérifier ».</div>
                  {preview.conflicts.slice(0, 5).map((c, i) => <div key={i}>{c.name} : {c.existing} → {c.incoming}</div>)}
                </div>
              )}
              <p className="text-xs text-muted mb-4">Les sociétés matchées seront enrichies, les nouvelles créées. Les conflits et cas ambigus vont dans la file « À vérifier » (aucun écrasement automatique d'un domaine vérifié).</p>
              <div className="flex items-center gap-3">
                <button onClick={() => setStep(1)} className="text-xs text-muted hover:text-fg flex items-center gap-1"><ArrowLeft size={12} /> Modifier le mapping</button>
                <button onClick={commit} disabled={busy} data-testid="import-commit-btn"
                  className="ml-auto flex items-center gap-2 bg-accent text-black font-semibold px-5 py-2.5 rounded-md hover-lift disabled:opacity-50">
                  <CheckCircle size={16} weight="bold" /> {busy ? "Import…" : "Importer"}
                </button>
              </div>
            </div>
          )}

          {/* STEP 3 — done */}
          {step === 3 && result && (
            <div className="text-center py-6" data-testid="import-step-done">
              <CheckCircle size={40} weight="fill" className="text-accent mx-auto mb-3" />
              <div className="font-display font-bold text-fg text-lg mb-1">Import terminé</div>
              <p className="text-sm text-muted mb-4">{result.enriched} sociétés enrichies · {result.created} créées · {result.same} identiques · {result.conflicts} conflits · {result.review} à vérifier · {result.invalid} invalides.</p>
              <button onClick={() => close(false)} className="bg-accent text-black font-semibold px-5 py-2.5 rounded-md hover-lift" data-testid="import-close-btn">Terminer</button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Pv({ label, value, color }) {
  return (
    <div className="surface-2 border border-border rounded-md p-3 text-center">
      <div className="text-xl font-display font-bold" style={{ color: color || "#F4F4F5" }}>{value ?? 0}</div>
      <div className="text-[11px] text-muted mt-0.5">{label}</div>
    </div>
  );
}
